import React, { useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import customerSatisfactionSurveyService from '@/services/customerSatisfactionSurvey.service';
import type { CustomerSatisfactionSurvey } from '@/constants/customer-satisfaction-survey';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Sent: 'processing',
  Pending: 'processing',
  Completed: 'success',
  Reviewed: 'cyan',
  Cancelled: 'error',
  Skipped: 'default',
};

const CustomerSatisfactionRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [result, setResult] = useState<string | undefined>();
  const [range, setRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const filters = useMemo(
    () => ({
      status,
      satisfaction_result: result,
      search: search || undefined,
      date_from: range?.[0]?.format('YYYY-MM-DD'),
      date_to: range?.[1]?.format('YYYY-MM-DD'),
    }),
    [status, result, search, range]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['customer-satisfaction', 'list', filters],
    queryFn: () => customerSatisfactionSurveyService.list(filters),
  });

  const rows = data?.rows ?? [];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <h2 style={{ margin: 0 }}>Customer Satisfaction Register</h2>
              <Text type="secondary">NTES-QSF-SAL-01 · Procedure NTES-QSP-SAL-001</Text>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => {
                    const q = new URLSearchParams();
                    if (status) q.set('status', status);
                    if (result) q.set('result', result);
                    if (search.trim()) q.set('search', search.trim());
                    if (filters.date_from) q.set('date_from', filters.date_from);
                    if (filters.date_to) q.set('date_to', filters.date_to);
                    const qs = q.toString();
                    navigate(`/print/growth/customer-satisfaction${qs ? `?${qs}` : ''}`);
                  }}
                >
                  Print register
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/growth/customer-satisfaction/new')}
                >
                  New survey
                </Button>
              </Space>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Search survey no / customer / project / SO"
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Status"
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Sent', label: 'Sent' },
                  { value: 'Completed', label: 'Completed' },
                  { value: 'Reviewed', label: 'Reviewed' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Result"
                value={result}
                onChange={setResult}
                options={[
                  { value: 'Satisfied', label: 'Satisfied' },
                  { value: 'Unsatisfied', label: 'Unsatisfied' },
                ]}
              />
            </Col>
            <Col xs={24} md={8}>
              <RangePicker style={{ width: '100%' }} value={range} onChange={(v) => setRange(v)} />
            </Col>
          </Row>

          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, total: data?.total }}
            onRow={(r) => ({
              onClick: () => navigate(`/growth/customer-satisfaction/${r.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: 'Survey no',
                dataIndex: 'survey_number',
                width: 140,
                render: (v) => v || '—',
              },
              {
                title: 'Customer',
                dataIndex: 'customer_name',
                render: (_: unknown, r: CustomerSatisfactionSurvey) =>
                  r.customer_name_snapshot || r.customer_name || '—',
              },
              {
                title: 'Project / PO',
                dataIndex: 'project_or_po_reference',
                ellipsis: true,
                render: (v) => v || '—',
              },
              {
                title: 'SO',
                dataIndex: 'sales_order_number',
                width: 120,
                render: (v) => v || '—',
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
              },
              {
                title: 'Score %',
                dataIndex: 'satisfaction_pct',
                width: 90,
                align: 'right',
                render: (v) => (v != null ? `${Number(v).toFixed(1)}%` : '—'),
              },
              {
                title: 'Result',
                dataIndex: 'satisfaction_result',
                width: 120,
                render: (v: string | null) =>
                  v ? (
                    <Tag color={v === 'Satisfied' ? 'success' : 'error'}>{v}</Tag>
                  ) : (
                    '—'
                  ),
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                width: 120,
                render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
              },
              {
                title: '',
                key: 'print',
                width: 48,
                render: (_: unknown, r: CustomerSatisfactionSurvey) => (
                  <Button
                    type="text"
                    size="small"
                    icon={<PrinterOutlined />}
                    aria-label={`Print ${r.survey_number || 'survey'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/print/growth/customer-satisfaction/${r.id}`);
                    }}
                  />
                ),
              },
            ]}
          />
        </Space>
      </Card>
    </div>
  );
};

export default CustomerSatisfactionRegisterPage;
